/**
 * CellValueViewer — rich modal for inspecting a single cell value.
 *
 * Tabs: Auto / JSON / Text / Hex / Image (the latter only when an image magic
 * number is detected). Auto picks the most useful tab for the value (see
 * lib/cellvalue.detectFormat). The viewer is read-only on a static result grid;
 * the editable result-grid + table-data grid can optionally save edits back
 * (apply prop is wired by the Grid).
 *
 * The differentiator vs other tools: cell-to-cell diff. Click "Pick for diff"
 * on cell A → close, open cell B → click "Diff against picked" → side-by-side
 * line diff. The pick is module-level (see pickedCell signal) so it survives
 * closing/reopening the viewer.
 */
import { createSignal, createMemo, For, Show } from 'solid-js';
import Modal from './Modal.jsx';
import {
  tryJSON, looksLikeMarkup, imageMimeOf, stringToBytes, hexDump,
  detectFormat, diffLines, restoreLineEndings,
} from '../lib/cellvalue.js';

// Module-level "pinned for diff" so it persists across viewer open/close.
const [pickedCell, setPickedCell] = createSignal(null); // { value, label }
export { pickedCell, setPickedCell };
export function clearPick() { setPickedCell(null); }

const TABS = ['auto', 'json', 'text', 'hex', 'image'];

export default function CellValueViewer(props) {
  // props: { open, onClose, value, label, editable?, onApply?(newText) }
  const valueStr = () => (props.value == null ? '' : String(props.value));
  const isNull = () => props.value == null;
  const fmt = () => detectFormat(props.value);
  const bytes = createMemo(() => stringToBytes(valueStr()));
  const mime = createMemo(() => imageMimeOf(bytes()));

  const [tab, setTab] = createSignal('auto');
  const effectiveTab = () => (tab() === 'auto' ? fmt() : tab());

  // Edit buffer (only used when editable).
  const [editing, setEditing] = createSignal(false);
  const [draft, setDraft]     = createSignal('');
  const startEdit = () => { setDraft(valueStr()); setEditing(true); };
  // The textarea reports CRLF line breaks as LF; restore them so applying an
  // untouched (or lightly edited) CRLF value doesn't rewrite every line break.
  const applyEdit = async () => {
    try { await props.onApply?.(restoreLineEndings(draft(), valueStr())); props.onClose?.(); }
    catch (e) { /* parent shows error */ console.warn(e); }
  };

  // Diff state
  const [diffOn, setDiffOn] = createSignal(false);
  const diffOps = createMemo(() => {
    if (!diffOn() || !pickedCell()) return null;
    return diffLines(pickedCell().value, valueStr());
  });

  const copyAll = async () => { try { await navigator.clipboard.writeText(valueStr()); } catch (_) {} };

  const imageURL = createMemo(() => {
    if (!mime()) return '';
    return URL.createObjectURL(new Blob([bytes()], { type: mime() }));
  });

  return (
    <Show when={props.open}>
      <Modal open={true} onClose={props.onClose} title={`Value · ${props.label || ''}`} wide>
        {/* Toolbar */}
        <div class="cv-toolbar">
          <div class="cv-tabs">
            <For each={TABS}>{(t) => (
              <Show when={t !== 'image' || mime()}>
                <button class="cv-tab" classList={{ active: tab() === t }} onClick={() => setTab(t)}>
                  {t === 'auto' ? `Auto (${fmt()})` : t.toUpperCase()}
                </button>
              </Show>
            )}</For>
          </div>
          <div class="cv-spacer" />
          <span class="muted mono cv-meta">{valueStr().length.toLocaleString()} chars</span>
          <button class="btn btn-sm btn-ghost" onClick={copyAll}>⧉ Copy</button>
          {/* Cell-to-cell diff — the unique angle. */}
          <button class="btn btn-sm btn-ghost"
                  onClick={() => setPickedCell({ value: valueStr(), label: props.label || '' })}
                  title="Remember this value; later open another cell and Diff against it.">
            ✚ Pick for diff
          </button>
          <Show when={pickedCell()}>
            <button class="btn btn-sm" disabled={diffOn()}
                    onClick={() => setDiffOn(true)}
                    title={`Compare against the value picked from "${pickedCell().label || 'cell'}"`}>
              Δ Diff vs picked
            </button>
          </Show>
          <Show when={props.editable && !editing()}>
            <button class="btn btn-sm" onClick={startEdit}>✎ Edit</button>
          </Show>
        </div>

        {/* Body */}
        <div class="cv-body">
          <Show when={diffOn() && diffOps()}>
            <div class="cv-diff-head muted">
              <span>− picked: <span class="mono">{pickedCell().label || '(unknown)'}</span></span>
              <span style="margin:0 14px;">→</span>
              <span>+ this: <span class="mono">{props.label || ''}</span></span>
              <button class="btn btn-sm btn-ghost" style="margin-left:auto;" onClick={() => setDiffOn(false)}>Close diff</button>
            </div>
            <div class="cv-diff mono">
              <For each={diffOps()}>
                {(op) => (
                  <div class={'cv-diff-line cv-diff-' + op.kind}>
                    {op.kind === 'same' ? '  ' : op.kind === 'del' ? '- ' : '+ '}
                    {op.kind === 'add' ? (op.b ?? '') : (op.a ?? '')}
                  </div>
                )}
              </For>
            </div>
          </Show>

          <Show when={!diffOn() && editing()}>
            <textarea class="input mono cv-edit" value={draft()}
                      onInput={(e) => setDraft(e.currentTarget.value)} />
            <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:10px;">
              <button class="btn btn-ghost" onClick={() => setEditing(false)}>Cancel</button>
              <button class="btn" onClick={applyEdit}>Apply</button>
            </div>
          </Show>

          <Show when={!diffOn() && !editing()}>
            <Show when={isNull()}>
              <div class="muted" style="padding:14px;">NULL</div>
            </Show>
            <Show when={!isNull() && effectiveTab() === 'json'}>
              <JsonView text={valueStr()} />
            </Show>
            <Show when={!isNull() && effectiveTab() === 'markup'}>
              <pre class="cv-pre mono">{valueStr()}</pre>
            </Show>
            <Show when={!isNull() && effectiveTab() === 'text'}>
              <pre class="cv-pre mono">{valueStr()}</pre>
            </Show>
            <Show when={!isNull() && effectiveTab() === 'hex'}>
              <pre class="cv-pre mono">{hexDump(bytes())}</pre>
            </Show>
            <Show when={!isNull() && effectiveTab() === 'image' && imageURL()}>
              <div class="cv-image-wrap"><img class="cv-image" src={imageURL()} alt="" /></div>
            </Show>
          </Show>
        </div>
      </Modal>
    </Show>
  );
}

/* JSON tree-ish view. Tries to render a syntax-highlighted, pretty-printed
   block; falls back to plain text if parse fails. Collapsible nodes are kept
   simple — no infinite-recursion gymnastics, just a top-level toggle. */
function JsonView(props) {
  const parsed = createMemo(() => tryJSON(props.text));
  return (
    <Show when={parsed()} fallback={<pre class="cv-pre mono">{props.text}</pre>}>
      <pre class="cv-pre mono cv-json">{JSON.stringify(parsed().value, null, 2)}</pre>
    </Show>
  );
}
